import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { 
  creerDevis, 
  creerFacture, 
  enregistrerPaiement, 
  getStatistiquesFinancieres,
  verifierFacturesEnRetard,
  accepterDevis,
  refuserDevis,
  STATUTS 
} from '../services/accountingService';

/**
 * Hook pour gérer les données comptables
 */

export const useAccounting = (db, options = {}) => {
  const [devis, setDevis] = useState([]);
  const [factures, setFactures] = useState([]);
  const [statistiques, setStatistiques] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Charge les devis avec filtrage optionnel
   */
  const chargerDevis = useCallback((filtres = {}) => {
    if (!db) return;

    try {
      setLoading(true);
      
      let devisQuery = query(collection(db, 'devis'), orderBy('dateCreation', 'desc'));
      
      // Appliquer les filtres
      if (filtres.clientId) {
        devisQuery = query(devisQuery, where('clientId', '==', filtres.clientId));
      }
      
      if (filtres.statut) {
        devisQuery = query(devisQuery, where('statut', '==', filtres.statut));
      }
      
      if (filtres.annee) {
        const debutAnnee = new Date(filtres.annee, 0, 1);
        const finAnnee = new Date(filtres.annee, 11, 31);
        devisQuery = query(
          devisQuery, 
          where('dateCreation', '>=', debutAnnee),
          where('dateCreation', '<=', finAnnee)
        );
      }

      const unsubscribe = onSnapshot(
        devisQuery,
        (snapshot) => {
          const devisData = snapshot.docs.map(doc => {
            const data = doc.data();
            
            // Fonction sécurisée pour convertir les timestamps
            const convertirTimestamp = (timestamp) => {
              if (!timestamp) return null;
              // Vérifier si c'est un Timestamp Firebase
              if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp && typeof timestamp.toDate === 'function') {
                return timestamp.toDate();
              }
              // Si c'est déjà une Date, la retourner
              if (timestamp instanceof Date) {
                return timestamp;
              }
              // Si c'est une chaîne ISO, la convertir
              if (typeof timestamp === 'string') {
                return new Date(timestamp);
              }
              // Sinon, retourner null
              return null;
            };
            
            return {
              id: doc.id,
              ...data,
              // Convertir les timestamps en dates de manière sécurisée
              dateCreation: convertirTimestamp(data.dateCreation),
              dateValidite: convertirTimestamp(data.dateValidite),
              dateFacturation: convertirTimestamp(data.dateFacturation)
            };
          });
          
          setDevis(devisData);
          setLoading(false);
        },
        (err) => {
          console.error('Erreur chargement devis:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      return unsubscribe;

    } catch (err) {
      console.error('Erreur configuration devis:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [db]);

  /**
   * Charge les factures mandat depuis stripeBillingInvoices
   */
  const chargerFactures = useCallback((filtres = {}) => {
    if (!db) return;

    try {
      setLoading(true);

      // Charger depuis stripeBillingInvoices au lieu de factures
      let facturesQuery = query(collection(db, 'stripeBillingInvoices'), orderBy('dateGeneration', 'desc'));

      // Appliquer les filtres
      if (filtres.clientId) {
        facturesQuery = query(facturesQuery, where('client.id', '==', filtres.clientId));
      }

      if (filtres.statut) {
        facturesQuery = query(facturesQuery, where('statut', '==', filtres.statut));
      }

      if (filtres.annee) {
        const debutAnnee = new Date(filtres.annee, 0, 1);
        const finAnnee = new Date(filtres.annee, 11, 31);
        facturesQuery = query(
          facturesQuery,
          where('dateGeneration', '>=', debutAnnee),
          where('dateGeneration', '<=', finAnnee)
        );
      }

      const unsubscribe = onSnapshot(
        facturesQuery,
        (snapshot) => {
          const facturesData = snapshot.docs.map(doc => {
            const data = doc.data();

            // Fonction sécurisée pour convertir les timestamps
            const convertirTimestamp = (timestamp) => {
              if (!timestamp) return null;
              // Vérifier si c'est un Timestamp Firebase
              if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp && typeof timestamp.toDate === 'function') {
                return timestamp.toDate();
              }
              // Si c'est déjà une Date, la retourner
              if (timestamp instanceof Date) {
                return timestamp;
              }
              // Si c'est une chaîne ISO, la convertir
              if (typeof timestamp === 'string') {
                return new Date(timestamp);
              }
              // Sinon, retourner null
              return null;
            };
            
            // Adapter la structure stripeBillingInvoices au format attendu par le dashboard
            const dateGeneration = convertirTimestamp(data.dateGeneration);

            // Calculer date d'échéance : si présente utiliser celle-là, sinon +30 jours après génération
            let dateEcheance = data.dateEcheance ? convertirTimestamp(data.dateEcheance) : null;
            if (!dateEcheance && dateGeneration) {
              dateEcheance = new Date(dateGeneration);
              dateEcheance.setDate(dateEcheance.getDate() + 30); // +30 jours par défaut
            }

            return {
              id: doc.id,
              ...data,
              // Adapter les champs pour compatibilité
              numero: doc.id, // Utiliser l'ID comme numéro de facture
              clientId: data.client?.id,
              clientNom: data.client?.nom || 'Client inconnu',
              dateEmission: dateGeneration, // Utiliser dateGeneration comme dateEmission
              dateGeneration: dateGeneration,
              dateEcheance: dateEcheance,
              datePaiement: convertirTimestamp(data.datePaiement),
              montantTTC: data.calcul?.montantTotal || 0,
              montantHT: data.calcul?.montantTotal || 0, // Pas de TVA dans le système mandat
              // Mapper les statuts
              statut: data.statut === 'payée' ? 'payee' :
                      data.statut === 'envoyée' ? 'en_attente' :
                      data.statut === 'annulée' ? 'annulee' :
                      'brouillon',
              // Calculer le retard si facture envoyée et non payée
              estEnRetard: (data.statut === 'envoyée' || data.statut === 'generée') &&
                          dateEcheance && dateEcheance < new Date(),
              // Filtrer les factures supprimées
              deleted: data.deleted || false
            };
          })
          .filter(facture => !facture.deleted); // Exclure les factures supprimées

          setFactures(facturesData);
          setLoading(false);
        },
        (err) => {
          console.error('Erreur chargement factures:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      return unsubscribe;

    } catch (err) {
      console.error('Erreur configuration factures:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [db]);

  /**
   * Charge les statistiques financières
   */
  const chargerStatistiques = useCallback(async (periode = {}) => {
    if (!db) return;

    try {
      setLoading(true);
      const stats = await getStatistiquesFinancieres(db, periode);
      setStatistiques(stats);
      setLoading(false);
    } catch (err) {
      console.error('Erreur chargement statistiques:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [db]);

  /**
   * Crée un nouveau devis
   */
  const creerNouveauDevis = useCallback(async (contrat, lignesPrestations, client, options = {}) => {
    if (!db) throw new Error('Base de données non disponible');

    try {
      setLoading(true);
      const nouveauDevis = await creerDevis(db, contrat, lignesPrestations, client, options);
      setLoading(false);
      return nouveauDevis;
    } catch (err) {
      console.error('Erreur création devis:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [db]);

  /**
   * Met à jour le statut d'un devis
   */
  const mettreAJourStatutDevis = useCallback(async (devisId, nouveauStatut, options = {}) => {
    if (!db) throw new Error('Base de données non disponible');

    try {
      setLoading(true);
      const devisRef = doc(db, 'devis', devisId);
      
      const updateData = {
        statut: nouveauStatut,
        dateModification: new Date()
      };
      
      if (options.notes) {
        updateData.notes = options.notes;
      }
      
      if (nouveauStatut === STATUTS.DEVIS.ENVOYE) {
        updateData.dateEnvoi = new Date();
      }
      
      if (nouveauStatut === STATUTS.DEVIS.ACCEPTE) {
        updateData.dateAcceptation = new Date();
      }
      
      await updateDoc(devisRef, updateData);
      setLoading(false);
      
    } catch (err) {
      console.error('Erreur mise à jour devis:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [db]);

  /**
   * Crée une facture à partir d'un devis
   */
  const creerNouvelleFacture = useCallback(async (devisId, options = {}) => {
    if (!db) throw new Error('Base de données non disponible');

    try {
      setLoading(true);
      const nouvelleFacture = await creerFacture(db, devisId, options);
      setLoading(false);
      return nouvelleFacture;
    } catch (err) {
      console.error('Erreur création facture:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [db]);

  /**
   * Enregistre un paiement
   */
  const enregistrerNouveauPaiement = useCallback(async (factureId, paiement) => {
    if (!db) throw new Error('Base de données non disponible');

    try {
      setLoading(true);
      const paiementEnregistre = await enregistrerPaiement(db, factureId, paiement);
      setLoading(false);
      return paiementEnregistre;
    } catch (err) {
      console.error('Erreur enregistrement paiement:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [db]);

  /**
   * Accepte un devis et convertit le prospect en client
   */
  const accepterNouveauDevis = useCallback(async (devisId, options = {}) => {
    if (!db) throw new Error('Base de données non disponible');

    try {
      setLoading(true);
      const resultat = await accepterDevis(db, devisId, options);
      setLoading(false);
      return resultat;
    } catch (err) {
      console.error('Erreur acceptation devis:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [db]);

  /**
   * Refuse un devis avec raison
   */
  const refuserNouveauDevis = useCallback(async (devisId, options = {}) => {
    if (!db) throw new Error('Base de données non disponible');

    try {
      setLoading(true);
      const resultat = await refuserDevis(db, devisId, options);
      setLoading(false);
      return resultat;
    } catch (err) {
      console.error('Erreur refus devis:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [db]);

  /**
   * Vérifie les factures en retard
   */
  const verifierFacturesRetard = useCallback(async () => {
    if (!db) throw new Error('Base de données non disponible');

    try {
      setLoading(true);
      const facturesEnRetard = await verifierFacturesEnRetard(db);
      setLoading(false);
      return facturesEnRetard;
    } catch (err) {
      console.error('Erreur vérification factures en retard:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [db]);

  /**
   * Obtient les devis par statut
   */
  const getDevisParStatut = useCallback((statut) => {
    return devis.filter(devis => devis.statut === statut);
  }, [devis]);

  /**
   * Obtient les factures par statut
   */
  const getFacturesParStatut = useCallback((statut) => {
    return factures.filter(facture => facture.statut === statut);
  }, [factures]);

  /**
   * Calcule le chiffre d'affaires mensuel
   */
  const getChiffreAffairesMensuel = useCallback(() => {
    const maintenant = new Date();
    const debutMois = new Date(maintenant.getFullYear(), maintenant.getMonth(), 1);
    const finMois = new Date(maintenant.getFullYear(), maintenant.getMonth() + 1, 0);
    
    const facturesMois = factures.filter(facture => {
      const dateEmission = facture.dateEmission;
      return dateEmission >= debutMois && dateEmission <= finMois;
    });
    
    return facturesMois.reduce((sum, facture) => sum + facture.montantTTC, 0);
  }, [factures]);

  /**
   * Recharge toutes les données
   */
  const rechargerDonnees = useCallback(() => {
    setError(null);
    chargerDevis();
    chargerFactures();
    chargerStatistiques();
  }, [chargerDevis, chargerFactures, chargerStatistiques]);

  /**
   * Charge les données au montage
   */
  useEffect(() => {
    if (!db) return;

    const unsubDevis = chargerDevis();
    const unsubFactures = chargerFactures();
    chargerStatistiques();

    return () => {
      if (unsubDevis) unsubDevis();
      if (unsubFactures) unsubFactures();
    };
  }, [db, chargerDevis, chargerFactures, chargerStatistiques]);

  return {
    // Données
    devis,
    factures,
    statistiques,
    loading,
    error,
    
    // Méthodes de chargement
    chargerDevis,
    chargerFactures,
    chargerStatistiques,
    rechargerDonnees,
    
    // Méthodes d'action
    creerNouveauDevis,
    mettreAJourStatutDevis,
    accepterNouveauDevis,
    refuserNouveauDevis,
    creerNouvelleFacture,
    enregistrerNouveauPaiement,
    verifierFacturesRetard,
    
    // Méthodes utilitaires
    getDevisParStatut,
    getFacturesParStatut,
    getChiffreAffairesMensuel,
    
    // Propriétés calculées
    devisEnCours: getDevisParStatut(STATUTS.DEVIS.BROUILLON),
    devisEnvoyes: getDevisParStatut(STATUTS.DEVIS.ENVOYE),
    devisAcceptes: getDevisParStatut(STATUTS.DEVIS.ACCEPTE),

    // Factures mandat - adapter aux statuts stripeBillingInvoices
    facturesEnAttente: factures.filter(f => f.statut === 'en_attente'), // envoyée
    facturesPayees: factures.filter(f => f.statut === 'payee'), // payée
    facturesEnRetard: factures.filter(f => f.estEnRetard === true),
    facturesGenerees: factures.filter(f => f.statut === 'brouillon'), // generée
    facturesAnnulees: factures.filter(f => f.statut === 'annulee'), // annulée

    totalEnRetard: factures
      .filter(f => f.estEnRetard === true)
      .reduce((sum, f) => sum + f.montantTTC, 0)
  };
};

export default useAccounting;
